import React, { useContext, useEffect, useState } from 'react'
import { generatePath } from 'react-router-dom'
import { ClientDto } from 'api/Clients/Types/Client'
import { ComplexTransfer } from 'api/Transaction/Types/ComplexTransfer'
import {
  CurrencyType,
  DragonPhoenixCurrencyType,
  PHPCurrencyType,
  WalletDto,
} from 'api/Wallet/Types/Wallet'
import { Spinner } from 'components/Spinner'
import { ToastContext } from 'components/Toast/Toast'
import { useAppDispatch, useAppSelector } from 'store'
import {
  fetchDirectionType,
  fetchTransactionTypeAction,
} from 'store/transactions/transaction.action'
import { useApiClient } from 'utils/api/ApiClient'
import { useCallbackWithForceRefresh } from 'utils/api/useCallbackWithForceRefresh'
import { useFetchOne } from 'utils/api/useFetch'
import { WalletsApiClient } from 'utils/api/walletsApiClient'
import { isEmpty } from 'utils/is-empty'

import { MultiAdjustmentForm, Transaction } from './MultiAdjustmentForm'

const useMapStateToProps = () => ({
  transactionTypes: useAppSelector((state) => state.transactions.types),
  directionType: useAppSelector((state) => state.transactions.directionType),
})

const useMapDispatchToProps = () => {
  const dispatch = useAppDispatch()

  const walletsApiClient = useApiClient(WalletsApiClient)

  return {
    fetchTransactionTypes: async () =>
      dispatch(fetchTransactionTypeAction(walletsApiClient, { tags: 'WalletAdjustments' })),
  }
}

interface Props {
  client: ClientDto | null
  preSelectedWallet?: WalletDto
  setClient(): void
}

const MultiAdjustmentPage: React.FC<Props> = ({ client, preSelectedWallet, setClient }) => {
  const dispatch = useAppDispatch()

  const setToast = useContext(ToastContext)
  const walletsApiClient = useApiClient(WalletsApiClient)

  const { transactionTypes, directionType } = useMapStateToProps()
  const { fetchTransactionTypes } = useMapDispatchToProps()

  const [transactions, setTransactions] = useState<Transaction[]>([])
  const [currency, setCurrency] = useState<
    null | CurrencyType | DragonPhoenixCurrencyType | PHPCurrencyType | string
  >(null)
  const [wallet, setWalletId] = useState<string>('')

  const { callback: fetchData, forceRefresh } = useCallbackWithForceRefresh(async () => {
    if (!client) {
      return undefined
    }
    return walletsApiClient.getClientWallet(client.id)
  }, [client])

  useEffect(() => {
    ;(async () => {
      await fetchTransactionTypes()
    })()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const addTransaction = (values: { directionTypeId: string; amount: number }) => {
    const { directionTypeId } = values
    const walletDto = (wallets || []).find((x) => x.id === wallet)

    if (client && directionTypeId && walletDto) {
      setTransactions([
        ...transactions,
        {
          id: new Date().getTime(),
          amount: values.amount,
          client: client.name,
          directionTypeId,
          wallet: walletDto,
        },
      ])
    }
  }

  const removeTransaction = (transactionId: number) => {
    setTransactions(transactions.filter((x) => x.id !== transactionId))
  }

  useEffect(() => {
    if (isEmpty(transactionTypes)) dispatch(fetchTransactionTypeAction(walletsApiClient))

    if (isEmpty(directionType)) dispatch(fetchDirectionType(walletsApiClient))
  }, [transactionTypes, directionType]) // eslint-disable-line react-hooks/exhaustive-deps

  const { data: wallets, isLoading } = useFetchOne(fetchData)

  useEffect(() => {
    if (preSelectedWallet) {
      setWallet(preSelectedWallet.id)
    }
  }, [wallets, preSelectedWallet]) // eslint-disable-line react-hooks/exhaustive-deps

  const onSubmit = async (values: ComplexTransfer) => {
    const response = await walletsApiClient.createListOfSingleWalletTransfer(values)
    setToast({
      type: 'success',
      requireInteraction: true,
      title: `Transaction has been submitted.`,
      subtitle: 'Click for details',
      url: generatePath('/transactions/:id', {
        id: response.id,
      }),
    })
    forceRefresh()
  }

  const setWallet = async (walletId: string) => {
    const walletDto = (wallets || []).find((x) => x.id === walletId)
    if (walletDto) {
      setWalletId(walletDto.id)
      setCurrency(walletDto.currency.id)
    }
  }

  if (isLoading) {
    return <Spinner />
  }

  return (
    <MultiAdjustmentForm
      directionType={directionType}
      transactionTypes={transactionTypes}
      walletId={wallet}
      setWallet={setWallet}
      transactions={transactions}
      addTransaction={addTransaction}
      removeTransaction={removeTransaction}
      currency={currency}
      setCurrency={setCurrency}
      client={client}
      setClient={setClient}
      wallets={wallets || []}
      onSubmit={onSubmit}
    />
  )
}

export default MultiAdjustmentPage
